import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const THU_VI = ["Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"];

export function unwrapBangKeoMatchList(response) {
  const payload = response?.data ?? response;
  if (Array.isArray(payload) && payload.length > 0 && Array.isArray(payload[0]?.matches)) {
    return payload.flatMap((g) => g.matches ?? []);
  }
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) {
    const inner = payload.data;
    if (inner.length > 0 && Array.isArray(inner[0]?.matches)) {
      return inner.flatMap((g) => g.matches ?? []);
    }
    return inner;
  }
  return [];
}

export function matchDateKey(match) {
  if (match?.date) return String(match.date);
  const t = Number(match?.matchTime);
  if (!Number.isFinite(t) || t <= 0) return "";
  return dayjs.unix(t).tz(TZ).format("YYYY-MM-DD");
}

export function groupMatchesByDate(matches, { withinDayDesc = false } = {}) {
  const map = new Map();
  for (const match of matches || []) {
    const key = matchDateKey(match);
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(match);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => {
      const ta = Number(a.matchTime) || 0;
      const tb = Number(b.matchTime) || 0;
      return withinDayDesc ? tb - ta : ta - tb;
    });
  }
  return map;
}

export function sortDateEntriesDesc(entries) {
  return [...(entries || [])].sort((a, b) => String(b[0]).localeCompare(String(a[0])));
}

export function sortDateEntriesAsc(entries) {
  return [...(entries || [])].sort((a, b) => String(a[0]).localeCompare(String(b[0])));
}

export function getDateGroupLabel(dateKey) {
  const d = dayjs.tz(`${String(dateKey)}T12:00:00`, TZ);
  if (!d.isValid()) return String(dateKey);
  return `${THU_VI[d.day()]}, ${d.format("DD/MM")}`;
}

export function filterMatchesInDateRange(matches, fromDate, toDate) {
  return (matches || []).filter((m) => {
    const key = matchDateKey(m);
    if (!key) return false;
    if (fromDate && key < fromDate) return false;
    if (toDate && key > toDate) return false;
    return true;
  });
}

export function groupMatchesByRound(matches, fallbackRound) {
  const map = new Map();
  for (const match of matches || []) {
    const r = match.round ?? fallbackRound;
    if (!map.has(r)) map.set(r, []);
    map.get(r).push(match);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => Number(a.matchTime) - Number(b.matchTime));
  }
  return map;
}

export function roundsFromCurrent(rounds, currentRound) {
  if (!Array.isArray(rounds) || !rounds.length) {
    return currentRound != null && currentRound !== "" ? [currentRound] : [];
  }
  const idx = rounds.findIndex((r) => String(r?.round ?? r) === String(currentRound));
  if (idx === -1) return [currentRound];
  return rounds.slice(idx).map((r) => r?.round ?? r);
}

export function keepRemainingMatches(matches, currentRound, rounds) {
  if (!Array.isArray(matches) || !matches.length) return [];
  if (Array.isArray(rounds) && rounds.length) {
    const remaining = new Set(roundsFromCurrent(rounds, currentRound).map(String));
    return matches.filter((m) => remaining.has(String(m?.round)));
  }
  const c = Number(currentRound);
  if (Number.isFinite(c)) {
    return matches.filter((m) => {
      const n = Number(m?.round);
      if (Number.isFinite(n)) return n >= c;
      return String(m?.round) === String(currentRound);
    });
  }
  if (currentRound == null || currentRound === "") return matches;
  return matches.filter((m) => String(m?.round) === String(currentRound));
}

export function sortRoundEntries(entries) {
  return [...(entries || [])].sort((a, b) => {
    const ta = Number(a[1]?.[0]?.matchTime) || 0;
    const tb = Number(b[1]?.[0]?.matchTime) || 0;
    if (ta !== tb) return ta - tb;
    const na = Number(a[0]);
    const nb = Number(b[0]);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a[0]).localeCompare(String(b[0]));
  });
}

export function getRoundLabel(round, matches) {
  const name = matches?.[0]?.roundName;
  if (name != null && String(name).trim() !== "") return String(name).trim();
  return `Vòng ${round}`;
}
